import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  ShoppingBag, MapPin, Shield, Check,
  ChevronRight, Truck, Lock, Star, Sparkles, ArrowRight,
  Phone, Mail, User, Gift
} from 'lucide-react';
import { BASE_URL, getImageUrl, getCart } from '../apiConfig';
import './CheckoutPage.css';

gsap.registerPlugin(ScrollTrigger);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [step, setStep] = useState(1); // 1=Address, 2=Payment, 3=Review
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);
  const [placeError, setPlaceError] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [showReviewSuccess, setShowReviewSuccess] = useState(false);
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', province: '', zip: '',
    cardName: '', cardNum: '', expiry: '', cvv: '',
    paymentMethod: 'cod'
  });
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const heroRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const orderConfirmRef = useRef(null);

  /* ── Normalize price from any format ── */
  const parsePrice = (price) => {
    if (!price) return 0;
    if (typeof price === 'number') return price;
    return parseInt(String(price).replace(/[^0-9]/g, '')) || 0;
  };

  /* ── Normalize image ── */
  const getImage = (item) => {
    if (item.imageSnapshot) return getImageUrl(item.imageSnapshot);
    const p = item.product || item;
    if (p.images && p.images.length > 0) return getImageUrl(p.images[0]);
    if (p.img) return getImageUrl(p.img);
    return 'https://via.placeholder.com/80x90?text=Fragrance';
  };

  /* ── Get Active Variant ID ── */
  const getActiveVariantId = (item) => {
    if (item.variantId) return item.variantId;
    if (item.variant && typeof item.variant === 'string') return item.variant;
    if (item.variant && typeof item.variant === 'object') return item.variant._id || item.variant.id;

    const p = item.product || item;
    const actualProductId = item.productId || item.product_id || (item.product && (item.product._id || item.product.id)) || (typeof item.product === 'string' ? item.product : null) || p._id || p.id;
    
    try {
      const userVariants = JSON.parse(localStorage.getItem('user_selected_variants') || '{}');
      if (actualProductId && userVariants[actualProductId]) return userVariants[actualProductId];
    } catch(e) {}

    return null;
  };

  /* ── Normalize price ── */
  const getPrice = (item) => {
    if (item.priceSnapshot && typeof item.priceSnapshot === 'number') return item.priceSnapshot;
    if (item.price && typeof item.price === 'number') return item.price;
    const p = item.product || item;
    
    const variantId = getActiveVariantId(item);
    if (variantId && p.variants && p.variants.length > 0) {
      const matched = p.variants.find(v => (v._id || v.id) === variantId);
      if (matched) return parsePrice(matched.finalPrice || matched.price);
    }

    if (item.variant && typeof item.variant === 'object') {
      const v = item.variant;
      if (v.finalPrice || v.price) return parsePrice(v.finalPrice || v.price);
    }
    
    const rawPrice = p.variants?.[0]?.finalPrice || p.variants?.[0]?.price || p.price || 0;
    
    const vol = getVolume(item).toLowerCase();
    let multiplier = 1;
    if (vol.includes('50ml') && (!p.variants || p.variants.length === 0)) multiplier = 0.6;
    if (vol.includes('200ml') && (!p.variants || p.variants.length === 0)) multiplier = 1.8;
    
    return Math.round(parsePrice(rawPrice) * multiplier);
  };

  /* ── Normalize volume ── */
  const getVolume = (item) => {
    const p = item.product || item;
    const actualProductId = item.productId || item.product_id || (item.product && (item.product._id || item.product.id)) || (typeof item.product === 'string' ? item.product : null) || p._id || p.id;
    
    const variantId = getActiveVariantId(item);

    // 1. HIGHEST PRIORITY: Local Storage (What user actually clicked)
    try {
      const userVols = JSON.parse(localStorage.getItem('user_selected_volumes') || '{}');
      if (actualProductId && variantId && userVols[`${actualProductId}_${variantId}`]) {
        return userVols[`${actualProductId}_${variantId}`];
      }
      if (actualProductId && userVols[actualProductId]) return userVols[actualProductId];
    } catch(e) {}

    // 2. MATCH VIA LOCALLY SAVED OR BACKEND VARIANT ID
    if (variantId && typeof variantId === 'string' && p.variants && p.variants.length > 0) {
      const matched = p.variants.find(v => (v._id || v.id) === variantId);
      if (matched) return matched.size || matched.volume || matched.name || matched.label || '100 ML';
    }
    
    // 3. BACKEND VARIANT OBJECT POPULATED
    if (item.variant && typeof item.variant === 'object') {
      const v = item.variant;
      const label = v.size || v.volume || v.name || v.label;
      if (label) return label;
    }

    // 4. BACKEND ITEM EXPLICIT VOLUME
    if (item.volume && typeof item.volume === 'string') return item.volume;
    if (item.size && typeof item.size === 'string') return item.size;

    // 5. FALLBACK
    return p.variants?.[0]?.size || p.variants?.[0]?.volume || p.variants?.[0]?.name || p.volume || '100 ML';
  };

  /* ── Normalize brand ── */
  // eslint-disable-next-line no-unused-vars
  const getBrand = (item) => {
    const p = item.product || item;
    return p.brand || p.collection || 'ESCENTRUM';
  };

  /* ── Normalize name ── */
  const getName = (item) => {
    if (item.titleSnapshot) return item.titleSnapshot;
    const p = item.product || item;
    return p.name || 'Unknown Product';
  };

  /* ── Merge duplicate cart items by product/variant ID ── */
  const mergeCartItems = (items) => {
    if (!Array.isArray(items)) return [];
    const map = new Map();
    items.forEach(item => {
      const pId = item.product?._id || item.product?.id || item._id || item.id;
      const vId = item.variantId || item.product?.variants?.[0]?._id || item.product?.variants?.[0]?.id || 'default';
      const key = `${pId}-${vId}`;
      const itemQty = item.qty !== undefined ? item.qty : (item.quantity || 1);

      if (map.has(key)) {
        const existing = map.get(key);
        const newQty = existing.qty + itemQty; existing.qty = newQty; existing.quantity = newQty;
      } else {
        map.set(key, { ...item, qty: itemQty, quantity: itemQty });
      }
    });
    return Array.from(map.values());
  };

  const total = cartItems.reduce((acc, item) => acc + getPrice(item) * (item.qty !== undefined ? item.qty : (item.quantity || 1)), 0);
  const tax = Math.round(total * 0.1);
  const shipping = total > 5000 ? 0 : 350;

  const getDiscountAmount = () => {
    if (!appliedCoupon) return 0;
    const type = appliedCoupon.discountType || '';
    const val = Number(appliedCoupon.value || appliedCoupon.discountValue || appliedCoupon.discountPercentage || appliedCoupon.discount || 0);
    if (type.toLowerCase() === 'percentage' || type.toLowerCase() === 'percent') {
      return Math.round(total * (val / 100));
    }
    return val;
  };

  const discountValue = getDiscountAmount();
  const grandTotal = Math.max(0, total + tax + shipping - discountValue);

  const handleApplyCoupon = (e) => {
    e.preventDefault();
    setCouponError('');
    setCouponSuccess('');
    
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code.');
      return;
    }

    const cleanedCode = couponCode.trim().toUpperCase();
    
    const coupon = availableCoupons.find(c => {
      const codeStr = c.code || c.couponCode || c.name || '';
      return codeStr.trim().toUpperCase() === cleanedCode;
    });

    if (!coupon) {
      setCouponError('Invalid coupon code.');
      setAppliedCoupon(null);
      return;
    }

    const minOrder = Number(coupon.minOrderAmount || coupon.minimumAmount || coupon.minOrder || 0);
    if (total < minOrder) {
      setCouponError(`Minimum order of PKR ${minOrder.toLocaleString()} required.`);
      setAppliedCoupon(null);
      return;
    }

    setAppliedCoupon(coupon);
    setCouponSuccess('Coupon applied successfully!');
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponSuccess('');
    setCouponError('');
  };

  useEffect(() => {
    window.scrollTo(0, 0);


    
    // Fetch cart from API (works for both logged-in and guest users)
    const fetchCartData = async () => {
      try {
        const cartData = await getCart();
        if (cartData && cartData.items && cartData.items.length > 0) {
          setCartItems(mergeCartItems(cartData.items));
          return;
        }
      } catch (err) {
        console.error("Error fetching cart in checkout:", err);
      }
      // Final fallback: empty cart
      setCartItems([]);
    };

    fetchCartData();

    // Fetch coupons for checkout validation (only if user is logged in)
    const fetchCoupons = async () => {
      const token = localStorage.getItem('token');
      // Don't attempt coupon fetch without a valid token - avoids 403 errors
      if (!token || token === 'simulated-token') return;
      
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

      const endpointsToTry = [
        `${BASE_URL}Coupon/all`,
        `${BASE_URL}Coupon/getAll`,
        `${BASE_URL}Coupon/getAllCoupons`,
        `${BASE_URL}Coupon/active`,
      ];

      for (const url of endpointsToTry) {
        try {
          const res = await fetch(url, { headers });
          if (!res.ok) continue;
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data.data) ? data.data
            : Array.isArray(data.coupons) ? data.coupons
            : Array.isArray(data.result) ? data.result
            : [];

          const activeCoupons = list.filter(c => {
            if (c.isActive === false) return false;
            const expiryDateStr = c.expiryDate || c.expiry || c.validUntil || c.endDate;
            if (expiryDateStr) {
              const expiryDate = new Date(expiryDateStr);
              const now = new Date();
              if (!isNaN(expiryDate.getTime()) && expiryDate < now) {
                return false; // Expired
              }
            }
            return true;
          });

          if (activeCoupons.length > 0) {
            setAvailableCoupons(activeCoupons);
            break;
          }
        } catch (err) {
          // ignore
        }
      }
    };
    fetchCoupons();

    // Pre-fill user data if available in localStorage
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (storedUser.email || storedUser.fullName) {
        setForm(prev => ({
          ...prev,
          firstName: storedUser.fullName ? storedUser.fullName.split(' ')[0] : prev.firstName,
          lastName: storedUser.fullName ? storedUser.fullName.split(' ').slice(1).join(' ') : prev.lastName,
          email: storedUser.email || prev.email,
          phone: storedUser.phone || prev.phone,
          address: storedUser.address || prev.address,
          city: storedUser.city || prev.city,
        }));
      }
    } catch {}

    // GSAP Entrance Animations
    const ctx = gsap.context(() => {
      gsap.from('.co-hero-badge', { y: -20, opacity: 0, duration: 0.8, ease: 'power3.out' });
      gsap.from('.co-hero h1', { y: 40, opacity: 0, duration: 1, delay: 0.2, ease: 'power4.out' });
      gsap.from('.co-steps-bar', { y: 20, opacity: 0, duration: 0.8, delay: 0.4, ease: 'power2.out' });
      gsap.from('.co-form-card', { x: -40, opacity: 0, duration: 0.9, delay: 0.5, ease: 'power3.out' });
      gsap.from('.co-order-summary', { x: 40, opacity: 0, duration: 0.9, delay: 0.6, ease: 'power3.out' });
      gsap.from('.co-trust-row', { y: 20, opacity: 0, duration: 0.8, delay: 0.8, ease: 'power2.out' });
      gsap.to('.co-glow-1', { x: '15%', y: '10%', duration: 9, ease: 'sine.inOut' });
      gsap.to('.co-glow-2', { x: '-15%', y: '-10%', duration: 11, ease: 'sine.inOut', delay: 1 });
    });
    return () => ctx.revert();
  }, []);

  const handleInput = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const nextStep = () => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (!token || !user) {
      window.dispatchEvent(new CustomEvent('loginRequired', {
        detail: { returnTo: '/checkout', message: 'Order place karne ke liye pehle login karein' }
      }));
      return;
    }
    setStep(s => s + 1);
    gsap.from('.co-form-card', { x: 30, opacity: 0, duration: 0.5, ease: 'power2.out' });
  };
  const prevStep = () => {
    setStep(s => s - 1);
    gsap.from('.co-form-card', { x: -30, opacity: 0, duration: 0.5, ease: 'power2.out' });
  };

  const placeOrder = async () => {
    setPlaceError('');
    setIsPlacing(true);
    const token = localStorage.getItem('token');

    const orderItems = cartItems.map(item => {
      const pId = item.product?._id || item.product?.id || item._id || item.id;
      const vId = item.variantId || item.product?.variants?.[0]?._id || item.product?.variants?.[0]?.id || null;
      const q = item.qty !== undefined ? item.qty : (item.quantity || 1);
      const pr = getPrice(item);
      const nm = getName(item);
      return {
        product: pId,
        productId: pId,
        variant: vId,
        variantId: vId,
        quantity: q,
        qty: q,
        price: pr,
        name: nm,
        volume: getVolume(item),
        size: getVolume(item)
      };
    });

    let orderPayload = {
      items: orderItems,
      products: orderItems,
      shippingAddress: `${form.address}, ${form.city}, ${form.province}, Pakistan. Phone: ${form.phone}`,
      address: form.address,
      city: form.city,
      province: form.province,
      phone: form.phone,
      email: form.email,
      fullName: `${form.firstName} ${form.lastName}`.trim(),
      paymentMethod: 'COD',
      subtotal: total,
      tax: tax,
      shippingCost: shipping,
      shipping: shipping,
      discount: discountValue,
      discountAmount: discountValue,
      couponCode: appliedCoupon ? (appliedCoupon.code || appliedCoupon.couponCode || appliedCoupon.name) : '',
      totalAmount: grandTotal,
      grandTotal: grandTotal,
      total: grandTotal,
      amount: grandTotal,
      orderDate: new Date().toISOString()
    };

    console.log('[CheckoutPage] Calling placeOrder API with payload:', orderPayload);

    try {
      let response = await fetch(`${BASE_URL}Order/placeOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(orderPayload)
      });

      console.log('[CheckoutPage] placeOrder API status (initial):', response.status);
      let data = await response.json().catch(() => ({}));
      console.log('[CheckoutPage] placeOrder API response (initial):', data);

      // If paymentMethod enum failed with 'COD', retry with 'Cash on Delivery'
      if (!response.ok && data.message && data.message.includes('paymentMethod')) {
        console.log('[CheckoutPage] Retrying placeOrder with paymentMethod: Cash on Delivery');
        orderPayload.paymentMethod = 'Cash on Delivery';
        response = await fetch(`${BASE_URL}Order/placeOrder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
          body: JSON.stringify(orderPayload)
        });
        data = await response.json().catch(() => ({}));
        console.log('[CheckoutPage] placeOrder API response (retry 1):', data);
      }

      // If paymentMethod enum failed again, retry with 'Cash On Delivery'
      if (!response.ok && data.message && data.message.includes('paymentMethod')) {
        console.log('[CheckoutPage] Retrying placeOrder with paymentMethod: Cash On Delivery');
        orderPayload.paymentMethod = 'Cash On Delivery';
        response = await fetch(`${BASE_URL}Order/placeOrder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
          body: JSON.stringify(orderPayload)
        });
        data = await response.json().catch(() => ({}));
        console.log('[CheckoutPage] placeOrder API response (retry 2):', data);
      }

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to place order. Please check your details.');
      }

      // Order placed successfully! Store snapshot in purchasedItems before clearing cart
      setPurchasedItems(cartItems);
      try {
        localStorage.setItem('purchasedItems', JSON.stringify(cartItems));
        
        // Cache this successful order locally to display instantly in dashboard
        const currentOrders = JSON.parse(localStorage.getItem('my_placed_orders') || '[]');
        const localOrderObj = {
          _id: data.data?._id || data.data?.id || `local-${Date.now()}`,
          id: data.data?._id || data.data?.id || `local-${Date.now()}`,
          orderId: data.data?.orderId || `ESC-00${10 + currentOrders.length + 1}`,
          createdAt: new Date().toISOString(),
          totalPrice: grandTotal,
          totalAmount: grandTotal,
          status: 'Processing',
          items: orderItems.map(item => ({
            ...item,
            product: item.product,
            productId: item.productId,
            name: item.name,
            price: item.price,
            qty: item.qty || item.quantity
          }))
        };
        localStorage.setItem('my_placed_orders', JSON.stringify([localOrderObj, ...currentOrders]));
      } catch (e) {
        console.error('[CheckoutPage] Failed to local-cache successfully placed order:', e);
      }

      if (token) {
        try {
          await fetch(`${BASE_URL}Cart`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (e) { console.error('Failed to clear backend cart:', e); }
      }
      localStorage.removeItem('cartItems');
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { totalQty: 0, totalItems: 0, source: 'checkout' } }));

      setIsPlacing(false);
      setOrderPlaced(true);
      window.scrollTo({ top: 0, behavior: 'instant' });
      setTimeout(() => {
        // Create an ultra-luxe sequential GSAP timeline for a WOW experience
        const tl = gsap.timeline();
        
        // 1. Reset and hide elements initially
        gsap.set('.co-confirm-wrap', { opacity: 1 });
        gsap.set('.co-confirm-icon-animated', { scale: 0, rotate: -180 });
        gsap.set('.co-confirm-text', { y: 50, opacity: 0 });
        gsap.set('.co-confirm-details', { scale: 0.9, opacity: 0 });
        gsap.set('.co-back-home-btn-luxe', { scale: 0.8, opacity: 0 });
        gsap.set('.co-gold-ring', { scale: 0, opacity: 0 });
        gsap.set('.co-sparkle', { x: 0, y: 0, scale: 0, opacity: 0, left: '50%', top: '22%' });

        // 2. Expand and rotate the gold rings
        tl.to('.co-gold-ring', {
          scale: (i) => [1, 1.4, 1.8][i] || 1,
          opacity: (i) => [0.6, 0.4, 0.2][i] || 0.3,
          duration: 1.5,
          stagger: 0.2,
          ease: 'power4.out'
        });

        // 3. Spin and pop the checkmark icon
        tl.to('.co-confirm-icon-animated', {
          scale: 1,
          rotate: 0,
          duration: 1.2,
          ease: 'elastic.out(1, 0.6)'
        }, '-=1.0');

        // 4. Burst the gold/diamond sparkles outwards
        tl.to('.co-sparkle', {
          x: () => (Math.random() - 0.5) * window.innerWidth * 0.9,
          y: () => (Math.random() - 0.55) * window.innerHeight * 0.75,
          scale: () => 0.4 + Math.random() * 1.6,
          opacity: () => 0.8 + Math.random() * 0.2,
          duration: () => 1.8 + Math.random() * 1.5,
          ease: 'power3.out',
          stagger: {
            amount: 0.3,
            from: 'center'
          }
        }, '-=0.8');

        // 5. Fade and slide up text headings
        tl.to('.co-confirm-text', {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.12,
          ease: 'power3.out'
        }, '-=1.2');

        // 6. Fade and scale up details box
        tl.to('.co-confirm-details', {
          scale: 1,
          opacity: 1,
          duration: 0.8,
          ease: 'back.out(1.5)'
        }, '-=0.6');

        // 7. Reveal and pulse the main call-to-action button
        tl.to('.co-back-home-btn-luxe', {
          scale: 1,
          opacity: 1,
          duration: 0.8,
          ease: 'elastic.out(1, 0.75)'
        }, '-=0.4');

        // 8. Continuous idle floating animation for background sparkles
        gsap.to('.co-sparkle', {
          y: '+=25',
          x: '+=12',
          duration: 4,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: 2
        });

        // 9. Slow rotational breathing of gold rings
        gsap.to('.co-gold-ring', {
          rotate: 360,
          duration: 25,
          repeat: -1,
          ease: 'none'
        });

      }, 100);

    } catch (err) {
      console.error('[CheckoutPage] placeOrder API catch error:', err);
      setIsPlacing(false);
      setPlaceError(err.message || 'Something went wrong while placing your order. Please try again.');
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!reviewComment.trim()) {
      setReviewError('Please write a comment for your review.');
      return;
    }
    setReviewError('');
    setReviewSubmitting(true);
    const token = localStorage.getItem('token');
    
    // Use purchasedItems first, fallback to cartItems
    const itemsSource = purchasedItems.length > 0 ? purchasedItems : cartItems;
    const item = itemsSource[0];
    let firstProduct = '';
    if (item) {
      if (item.productId && typeof item.productId === 'object') firstProduct = item.productId._id || item.productId.id;
      else if (item.productId && typeof item.productId === 'string') firstProduct = item.productId;
      else if (item.product && typeof item.product === 'object') firstProduct = item.product._id || item.product.id;
      else if (item.product && typeof item.product === 'string') firstProduct = item.product;
      else firstProduct = item._id || item.id || '';
    }

    const payload = {
      productId: firstProduct,
      product_id: firstProduct, // fallback with underscore
      product: firstProduct, // fallback
      id: firstProduct, // fallback
      rating: reviewRating,
      comment: reviewComment,
      review: reviewComment, // fallback
      title: 'Verified Customer Review',
      name: form.firstName ? `${form.firstName} ${form.lastName}`.trim() : 'Valued Customer',
      email: form.email || 'customer@escen.com'
    };

    console.log('[CheckoutPage] Submitting review with payload:', payload);

    try {
      // Try 1: Send in body + query params
      let response = await fetch(`${BASE_URL}Review/addReview?productId=${firstProduct}&product_id=${firstProduct}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(payload)
      });

      let data = await response.json().catch(() => ({}));

      // Try 2: If failed with 400/404, retry with URL Path parameter (/addReview/:productId)
      if (!response.ok) {
        console.log('[CheckoutPage] Retrying addReview with URL path parameter:', firstProduct);
        response = await fetch(`${BASE_URL}Review/addReview/${firstProduct}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: JSON.stringify(payload)
        });
        data = await response.json().catch(() => ({}));
      }

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to submit review.');
      }

      // Cache review in localStorage for instant UserDashboard display
      try {
        const addedRev = data.data || { ...payload, _id: 'rev-' + Date.now(), createdAt: new Date().toISOString() };
        const existing = JSON.parse(localStorage.getItem('my_recent_reviews') || '[]');
        localStorage.setItem('my_recent_reviews', JSON.stringify([addedRev, ...existing]));
      } catch (e) { console.error('Failed to cache review locally:', e); }

      // Success! Close modal and show animated success popup
      setReviewSubmitting(false);
      setShowReviewModal(false);
      setShowReviewSuccess(true);
      setReviewComment('');
      setReviewRating(5);

      // GSAP bounce animation for success popup
      setTimeout(() => {
        gsap.fromTo('.co-success-popup-box', 
          { scale: 0.5, opacity: 0, y: 50 }, 
          { scale: 1, opacity: 1, y: 0, duration: 0.6, ease: 'back.out(1.7)' }
        );
      }, 50);

    } catch (err) {
      console.error('[CheckoutPage] addReview error:', err);
      setReviewSubmitting(false);
      setReviewError(err.message || 'Something went wrong while submitting your review.');
    }
  };

  if (orderPlaced) {
    return (
      <div className="co-page co-celebration-active" style={{ overflow: 'hidden' }}>
        {/* ── LUXURY GOLD & DIAMOND CELEBRATION BACKGROUND ── */}
        <div className="co-luxury-celebration-bg" style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <div className="co-gold-ring co-ring-1"></div>
          <div className="co-gold-ring co-ring-2"></div>
          <div className="co-gold-ring co-ring-3"></div>
          <div className="co-sparkles-container" style={{ position: 'absolute', inset: 0 }}>
            {[...Array(55)].map((_, i) => (
              <div 
                key={i} 
                className={`co-sparkle co-sparkle-${i % 3}`}
                style={{
                  left: '50%',
                  top: '22%',
                  position: 'absolute',
                  transform: 'translate(-50%, -50%)'
                }}
              />
            ))}
          </div>
        </div>

        <div className="co-bg-glows">
          <div className="co-glow co-glow-1" />
          <div className="co-glow co-glow-2" />
        </div>
        <div className="co-confirm-wrap">
          <div className="co-confirm-icon-animated">
            <svg className="ep-checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
              <circle className="ep-checkmark__circle" cx="26" cy="26" r="25" fill="none" />
              <path className="ep-checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
          </div>
          <p className="co-confirm-text co-confirm-tag">ORDER CONFIRMED</p>
          <h1 className="co-confirm-text">Your Collection is Being Prepared</h1>
          <p className="co-confirm-text co-confirm-sub">
            A confirmation has been sent to <strong>{form.email || 'your email'}</strong>.<br />
            Our concierge team will reach you within 24 hours.
          </p>
          <div className="co-confirm-details co-confirm-text">
            <div className="co-confirm-row"><span>Order Total</span><span>PKR {grandTotal.toLocaleString()}</span></div>
            <div className="co-confirm-row"><span>Delivery to</span><span>{form.city || 'Pakistan'}</span></div>
            <div className="co-confirm-row"><span>Est. Delivery</span><span>3-5 Business Days</span></div>
          </div>
          <div className="co-confirm-buttons" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <button className="co-back-home-btn-luxe" onClick={() => navigate('/')}>
              RETURN TO BOUTIQUE <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* ── REVIEW MODAL ── */}
        {showReviewModal && (
          <div className="co-review-modal-overlay" onClick={() => setShowReviewModal(false)}>
            <div className="co-review-modal-box" onClick={e => e.stopPropagation()}>
              <div className="co-review-modal-head">
                <Star size={24} color="#d4af37" fill="#d4af37" />
                <h3>Rate Your Experience</h3>
                <p>We value your feedback on our luxury collection</p>
              </div>
              {reviewError && <div className="co-error-banner">{reviewError}</div>}
              <form onSubmit={handleAddReview} className="co-review-form">
                <div className="co-rating-select">
                  <label>Select Rating</label>
                  <div className="co-stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button 
                        type="button" 
                        key={star} 
                        className={`co-star-btn ${star <= reviewRating ? 'active' : ''}`}
                        onClick={() => setReviewRating(star)}
                      >
                        <Star size={28} color={star <= reviewRating ? '#d4af37' : '#555'} fill={star <= reviewRating ? '#d4af37' : 'none'} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="co-field">
                  <label>Your Review</label>
                  <textarea 
                    value={reviewComment} 
                    onChange={e => setReviewComment(e.target.value)} 
                    placeholder="Tell us what you loved about your experience..."
                    rows={4}
                    required
                  />
                </div>
                <div className="co-review-modal-actions">
                  <button type="button" className="co-cancel-btn" onClick={() => setShowReviewModal(false)} disabled={reviewSubmitting}>Cancel</button>
                  <button type="submit" className="co-submit-review-btn" disabled={reviewSubmitting}>
                    {reviewSubmitting ? <span className="co-spinner" /> : 'SUBMIT REVIEW'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── REVIEW SUCCESS POPUP ── */}
        {showReviewSuccess && (
          <div className="co-success-popup-overlay" onClick={() => setShowReviewSuccess(false)}>
            <div className="co-success-popup-box" onClick={e => e.stopPropagation()}>
              <div className="co-success-popup-icon">
                <Sparkles size={45} color="#d4af37" />
              </div>
              <h3>Thank You for Your Review!</h3>
              <p>Your feedback helps us curate the ultimate luxury experience.</p>
              <button className="co-popup-close-btn" onClick={() => setShowReviewSuccess(false)}>
                CONTINUE EXPLORING
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="co-page">
      {/* Background Glows */}
      <div className="co-bg-glows">
        <div className="co-glow co-glow-1" />
        <div className="co-glow co-glow-2" />
      </div>

      {/* Hero */}
      <section className="co-hero" ref={heroRef}>
        <div className="co-hero-overlay" />
        <div className="container">
          <div className="co-hero-badge">
            <Sparkles size={14} color="#d4af37" />
            <span>SECURE CHECKOUT</span>
          </div>
          <h1>Complete Your <span className="co-gold">Order</span></h1>
          <p>Authenticated luxury, delivered to your doorstep.</p>
        </div>
      </section>

      {/* Step Progress Bar */}
      <div className="co-steps-bar">
        <div className="container">
          <div className="co-steps-inner">
            {['Delivery', 'Payment', 'Review'].map((label, i) => (
              <React.Fragment key={i}>
                <div className={`co-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
                  <div className="co-step-num">
                    {step > i + 1 ? <Check size={14} /> : i + 1}
                  </div>
                  <span>{label}</span>
                </div>
                {i < 2 && <div className={`co-step-line ${step > i + 1 ? 'done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="co-main">
        <div className="container">
          <div className="co-layout">

            {/* ── FORM SECTION ── */}
            <div className="co-form-card">

              {/* STEP 1: DELIVERY */}
              {step === 1 && (
                <div className="co-form-step">
                  <div className="co-form-head">
                    <MapPin size={22} color="#d4af37" />
                    <h2>Delivery Address</h2>
                  </div>
                  <div className="co-fields">
                    <div className="co-field-row">
                      <div className="co-field">
                        <label>First Name</label>
                        <div className="co-input-wrap">
                          <User size={18} className="co-input-icon" />
                          <input name="firstName" value={form.firstName} onChange={handleInput} placeholder="e.g. Ahmed" />
                        </div>
                      </div>
                      <div className="co-field">
                        <label>Last Name</label>
                        <div className="co-input-wrap">
                          <User size={18} className="co-input-icon" />
                          <input name="lastName" value={form.lastName} onChange={handleInput} placeholder="e.g. Khan" />
                        </div>
                      </div>
                    </div>
                    <div className="co-field">
                      <label>Email Address</label>
                      <div className="co-input-wrap">
                        <Mail size={18} className="co-input-icon" />
                        <input name="email" type="email" value={form.email} onChange={handleInput} placeholder="ahmed@email.com" />
                      </div>
                    </div>
                    <div className="co-field">
                      <label>Phone Number</label>
                      <div className="co-input-wrap">
                        <Phone size={18} className="co-input-icon" />
                        <input name="phone" value={form.phone} onChange={handleInput} placeholder="+92 300 000 0000" />
                      </div>
                    </div>
                    <div className="co-field">
                      <label>Street Address</label>
                      <div className="co-input-wrap">
                        <MapPin size={18} className="co-input-icon" />
                        <input name="address" value={form.address} onChange={handleInput} placeholder="House #, Street, Area" />
                      </div>
                    </div>
                    <div className="co-field-row">
                      <div className="co-field">
                        <label>City</label>
                        <input name="city" value={form.city} onChange={handleInput} placeholder="Karachi" />
                      </div>
                      <div className="co-field">
                        <label>Province</label>
                        <select name="province" value={form.province} onChange={handleInput}>
                          <option value="">Select Province</option>
                          <option>Sindh</option>
                          <option>Punjab</option>
                          <option>KPK</option>
                          <option>Balochistan</option>
                          <option>Islamabad</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <button className="co-next-btn" onClick={nextStep}>
                    CONTINUE TO PAYMENT <ChevronRight size={18} />
                  </button>
                </div>
              )}

              {/* STEP 2: PAYMENT */}
              {step === 2 && (
                <div className="co-form-step">
                  <div className="co-form-head">
                    <Truck size={22} color="#d4af37" />
                    <h2>Payment Method</h2>
                  </div>
                  <div className="co-cod-msg">
                    <Truck size={40} color="#d4af37" strokeWidth={1} />
                    <h3>Cash on Delivery</h3>
                    <p>Pay in cash when your order arrives at your doorstep. Our courier will contact you before delivery.</p>
                  </div>
                  <div className="co-secure-note"><Shield size={14} /><span>Safe & secure delivery across Pakistan.</span></div>
                  <div className="co-btn-row">
                    <button className="co-back-btn" onClick={prevStep}>← BACK</button>
                    <button className="co-next-btn" onClick={nextStep}>REVIEW ORDER <ChevronRight size={18} /></button>
                  </div>
                </div>
              )}

              {/* STEP 3: REVIEW */}
              {step === 3 && (
                <div className="co-form-step">
                  <div className="co-form-head">
                    <Star size={22} color="#d4af37" />
                    <h2>Review & Place Order</h2>
                  </div>
                  {placeError && (
                    <div className="co-error-banner">
                      {placeError}
                    </div>
                  )}
                  <div className="co-review-block">
                    <div className="co-review-section">
                      <div className="co-review-head"><MapPin size={16} /><span>Delivery To</span></div>
                      <p>{form.firstName} {form.lastName}</p>
                      <p className="co-review-light">{form.address}, {form.city}, {form.province}</p>
                      <p className="co-review-light">{form.phone} | {form.email}</p>
                    </div>
                    <div className="co-review-section">
                      <div className="co-review-head"><Truck size={16} /><span>Payment Method</span></div>
                      <p>Cash on Delivery (COD)</p>
                    </div>
                    <div className="co-review-items">
                      {cartItems.map((item, i) => (
                        <div key={i} className="co-review-item">
                          <img src={getImage(item)} alt={getName(item)} />
                          <div>
                            <span>{getName(item)}</span>
                            <span className="co-review-light">Qty: {item.qty !== undefined ? item.qty : (item.quantity || 1)} · PKR {(getPrice(item) * (item.qty !== undefined ? item.qty : (item.quantity || 1))).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="co-btn-row">
                    <button className="co-back-btn" onClick={prevStep} disabled={isPlacing}>← BACK</button>
                    <button className="co-place-btn" onClick={placeOrder} disabled={isPlacing}>
                      {isPlacing ? <span className="co-spinner" /> : <Lock size={16} />} 
                      {isPlacing ? 'PLACING ORDER...' : `PLACE ORDER · PKR ${grandTotal.toLocaleString()}`}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── ORDER SUMMARY ── */}
            <div className="co-order-summary">
              <div className="co-summary-card">
                <div className="co-summary-title">
                  <ShoppingBag size={18} color="#d4af37" />
                  <h3>ORDER SUMMARY</h3>
                </div>
                <div className="co-summary-items">
                  {cartItems.map((item, i) => (
                    <div key={i} className="co-sum-item">
                      <div className="co-sum-img">
                        <img src={getImage(item)} alt={getName(item)} />
                        <span className="co-sum-qty">{item.qty !== undefined ? item.qty : (item.quantity || 1)}</span>
                      </div>
                      <div className="co-sum-info">
                        <p>{getName(item)}</p>
                        <span>{getVolume(item)}</span>
                      </div>
                      <span className="co-sum-price">PKR {(getPrice(item) * (item.qty !== undefined ? item.qty : (item.quantity || 1))).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="co-sum-divider" />
                
                {/* ── LUXURY COUPON INPUT FIELD ── */}
                <div className="co-checkout-coupon-section">
                  {!appliedCoupon ? (
                    <form onSubmit={handleApplyCoupon} className="co-coupon-form">
                      <div className="co-coupon-input-group">
                        <Gift className="co-coupon-gift-icon" size={16} />
                        <input
                          type="text"
                          placeholder="ENTER GIFT CODE / COUPON"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          className="co-coupon-input"
                        />
                        <button type="submit" className="co-coupon-apply-btn">
                          APPLY
                        </button>
                      </div>
                      {couponError && <p className="co-coupon-msg co-coupon-err">{couponError}</p>}
                    </form>
                  ) : (
                    <div className="co-applied-coupon-badge">
                      <div className="co-applied-coupon-info">
                        <Sparkles size={14} className="co-applied-sparkle" />
                        <div>
                          <p className="co-applied-code-title">COUPON APPLIED: {appliedCoupon.code || appliedCoupon.couponCode || appliedCoupon.name}</p>
                          <p className="co-applied-desc">
                            - PKR {discountValue.toLocaleString()} Saved
                          </p>
                        </div>
                      </div>
                      <button type="button" onClick={handleRemoveCoupon} className="co-coupon-remove-btn">
                        REMOVE
                      </button>
                    </div>
                  )}
                  {couponSuccess && <p className="co-coupon-msg co-coupon-succ">{couponSuccess}</p>}
                </div>

                <div className="co-sum-divider" />
                <div className="co-sum-rows">
                  <div className="co-sum-row"><span>Subtotal</span><span>PKR {total.toLocaleString()}</span></div>
                  <div className="co-sum-row"><span>Tax (10%)</span><span>PKR {tax.toLocaleString()}</span></div>
                  <div className="co-sum-row"><span>Shipping</span><span className={shipping === 0 ? 'co-free' : ''}>{shipping === 0 ? 'FREE' : `PKR ${shipping.toLocaleString()}`}</span></div>
                  {appliedCoupon && (
                    <div className="co-sum-row co-discount-row">
                      <span>Discount ({appliedCoupon.code || appliedCoupon.couponCode || appliedCoupon.name})</span>
                      <span className="co-discount-amount">- PKR {discountValue.toLocaleString()}</span>
                    </div>
                  )}
                </div>
                <div className="co-sum-divider" />
                <div className="co-sum-total">
                  <span>Grand Total</span>
                  <span>PKR {grandTotal.toLocaleString()}</span>
                </div>
                {shipping > 0 && (
                  <div className="co-free-shipping-msg">
                    <Gift size={14} /> Add PKR {(5000 - total).toLocaleString()} more for FREE shipping
                  </div>
                )}
              </div>

              {/* Trust Badges */}
              <div className="co-trust-row">
                <div className="co-trust-item"><Shield size={20} color="#d4af37" /><span>100% Authentic</span></div>
                <div className="co-trust-item"><Truck size={20} color="#d4af37" /><span>Free Returns</span></div>
                <div className="co-trust-item"><Lock size={20} color="#d4af37" /><span>Secure Payment</span></div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
